/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * "Next best actions" card for a lead or deal detail page: what the agent
 * working the record should do next to win it, suggested by the AI Assistant
 * from the record's facts and recent activity.
 *
 * Renders nothing unless the user may use the AI Assistant. Suggestions are
 * kept per record, so opening the page again costs nothing; "Refresh" makes
 * new ones. "Add as task" creates an ordinary activity on the record,
 * assigned to the current user, exactly as Add Activity would.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Card, Chip, CircularProgress, Divider } from "@mui/material";
import {
  AutoAwesome,
  Call as CallIcon,
  CheckCircle,
  Email as EmailIcon,
  Groups as MeetingIcon,
  Refresh,
  TaskAlt as TaskIcon,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import colors from "styles/tokens/colors";
import { toLocalDateTimeApi } from "utils/DateTimeConversion";
import { getApiErrorMessage } from "utils/apiErrors";
import useAiNextActions from "./useAiNextActions";

const PRIORITY = {
  high: { label: "High", ...colors.status.error },
  medium: { label: "Medium", ...colors.status.warning },
  low: { label: "Low", ...colors.status.neutral },
};

const TYPE_ICON = { Call: CallIcon, Email: EmailIcon, Meeting: MeetingIcon, Task: TaskIcon };

function dueLabel(days) {
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

// A suggestion becomes an activity. Meetings need a time slot the suggestion
// does not have, so they become a task to book one.
export function activityFor(action, objectApiName, recordId, user) {
  const due = new Date();
  due.setDate(due.getDate() + (Number(action.due_in_days) || 0));
  due.setHours(9, 0, 0, 0);
  const isMeeting = action.type === "Meeting";
  return {
    title: isMeeting ? `Book meeting: ${action.title}` : action.title,
    type: isMeeting ? "Task" : action.type,
    status: "Scheduled",
    description: action.why ? `Suggested by the AI Assistant: ${action.why}` : "Suggested by the AI Assistant.",
    due_date: toLocalDateTimeApi(due),
    parent_table: objectApiName,
    parent_table_id: Number(recordId),
    assigned_to: user?.id,
    assigned_to_name: user?.fullname,
    starttime: null,
    endtime: null,
    all_day: false,
  };
}

export default function NextBestActions({ objectApiName, recordId, canAddTask, onTaskCreated }) {
  const { axiosInstance, user } = useAuth();
  const { enabled, suggestion, loading, working, error, suggest } = useAiNextActions(objectApiName, recordId);
  const [added, setAdded] = useState({});
  const [adding, setAdding] = useState(null);
  const [taskError, setTaskError] = useState("");

  if (!enabled || !recordId) return null;

  const actions = suggestion?.actions || [];

  const refresh = async () => {
    setTaskError("");
    if (await suggest()) setAdded({});
  };

  const addTask = async (action, index) => {
    setAdding(index);
    setTaskError("");
    try {
      const res = await axiosInstance.post("/activities/", activityFor(action, objectApiName, recordId, user));
      setAdded((prev) => ({ ...prev, [index]: true }));
      if (onTaskCreated) onTaskCreated(res.data);
    } catch (err) {
      setTaskError(getApiErrorMessage(err, "Could not add the task. Please try again."));
    } finally {
      setAdding(null);
    }
  };

  return (
    <Card sx={{ mb: 5 }}>
      <MDBox
        mx={2}
        mt={-3}
        py={2}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <AutoAwesome sx={{ color: "white !important", mr: 1 }} />
          <MDTypography variant="h6" color="white" noWrap>
            Next best actions
          </MDTypography>
        </Box>
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          onClick={refresh}
          disabled={working}
          startIcon={working ? <CircularProgress size={12} color="inherit" /> : suggestion ? <Refresh /> : <AutoAwesome />}
          sx={{ flexShrink: 0, "&:disabled, &.Mui-disabled": { opacity: 0.8, color: "rgba(255, 255, 255, 0.85)" } }}
        >
          {working ? "Thinking..." : suggestion ? "Refresh" : "Suggest"}
        </MDButton>
      </MDBox>

      <MDBox p={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} color="info" />
          </Box>
        ) : !suggestion ? (
          <MDTypography variant="button" color="text" fontWeight="regular" display="block">
            Get suggested next steps for this {objectApiName === "deals" ? "deal" : "lead"}, based on its stage,
            value and recent activity.
          </MDTypography>
        ) : (
          <>
            {suggestion.summary && (
              <MDTypography variant="button" fontWeight="medium" color="dark" display="block" sx={{ mb: 1.5 }}>
                {suggestion.summary}
              </MDTypography>
            )}
            {actions.map((action, index) => {
              const priority = PRIORITY[action.priority] || PRIORITY.medium;
              const Icon = TYPE_ICON[action.type] || TaskIcon;
              return (
                <Box key={`${action.title}-${index}`}>
                  {index > 0 && <Divider sx={{ my: 1.25 }} />}
                  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                    <Icon sx={{ fontSize: 18, mt: 0.25, color: "text.secondary" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <MDTypography variant="button" fontWeight="bold" color="dark" display="block" sx={{ lineHeight: 1.35 }}>
                        {action.title}
                      </MDTypography>
                      {action.why && (
                        <MDTypography variant="caption" color="text" display="block" sx={{ mt: 0.25 }}>
                          {action.why}
                        </MDTypography>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 0.75 }}>
                        <Chip
                          size="small"
                          label={priority.label}
                          sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, bgcolor: priority.background, color: priority.text }}
                        />
                        <MDTypography variant="caption" color="text">
                          {action.type} · {dueLabel(action.due_in_days)}
                        </MDTypography>
                        {canAddTask && (
                          <Box sx={{ ml: "auto" }}>
                            {added[index] ? (
                              <MDTypography variant="caption" fontWeight="medium" color="success" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                <CheckCircle sx={{ fontSize: 14 }} /> Added
                              </MDTypography>
                            ) : (
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={() => addTask(action, index)}
                                disabled={adding !== null}
                                sx={{ py: 0.25, px: 1.25, minHeight: 0 }}
                              >
                                {adding === index ? "Adding..." : "Add as task"}
                              </MDButton>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <MDTypography variant="caption" color="text" display="block" sx={{ mt: 2 }}>
              Suggested {formatWhen(suggestion.generated_at)}
              {suggestion.stale ? ". This record has changed since; refresh for up-to-date steps." : "."}
            </MDTypography>
          </>
        )}
        {(error || taskError) && (
          <MDTypography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
            {error || taskError}
          </MDTypography>
        )}
      </MDBox>
    </Card>
  );
}

NextBestActions.propTypes = {
  objectApiName: PropTypes.oneOf(["leads", "deals"]).isRequired,
  recordId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Offer "Add as task": the tenant has activities and the record is not locked.
  canAddTask: PropTypes.bool,
  onTaskCreated: PropTypes.func,
};

NextBestActions.defaultProps = { recordId: null, canAddTask: false, onTaskCreated: null };
